import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Eye, Download, Search, Filter, X, FileJson, Calendar, SortAsc, SortDesc, Mail, Code } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FormSubmissions() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actor, setActor] = useState(null);
  const [ipCountries, setIpCountries] = useState({}); // Cache for IP to country mapping
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedCode, setEmbedCode] = useState("");
  
  // Filtering and Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState("submitted_at");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchActor();
    fetchSubmissions();
  }, [actorId]);

  useEffect(() => {
    applyFilters();
  }, [submissions, searchQuery, dateFrom, dateTo, sortField, sortOrder]);

  const fetchActor = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (error) {
      console.error("Error fetching actor:", error);
      toast.error("Failed to load AI Actor configuration");
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/admin/actors/${actorId}/form-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const submissionsData = response.data.submissions || [];
      setSubmissions(submissionsData);
      
      // Fetch country data for all unique IPs
      const uniqueIps = [...new Set(submissionsData.map(s => s.ip_address).filter(ip => ip))];
      fetchCountriesForIps(uniqueIps);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load form submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountriesForIps = async (ips) => {
    const newCountries = {};
    
    for (const ip of ips) {
      if (ipCountries[ip]) {
        newCountries[ip] = ipCountries[ip];
        continue;
      }
      
      // Skip private/local IPs - just mark them
      if (!ip || ip.startsWith('10.') || ip.startsWith('192.168.') || ip.startsWith('172.') || ip === '127.0.0.1' || ip.includes(':')) {
        newCountries[ip] = 'N/A (Private IP)';
        continue;
      }
      
      try {
        // Using ipwhois.io with https (free, 10k req/month)
        const response = await axiosInstance.get(`https://ipwho.is/${ip}`, {
          timeout: 3000
        });
        if (response.data && response.data.success) {
          newCountries[ip] = response.data.country || 'Unknown';
        } else {
          newCountries[ip] = 'Unknown';
        }
      } catch (error) {
        console.error(`Error fetching country for IP ${ip}:`, error);
        newCountries[ip] = 'Unknown';
      }
      
      // Add small delay to respect rate limit
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    setIpCountries(prev => ({ ...prev, ...newCountries }));
  };

  const getFieldLabel = (fieldId) => {
    if (!actor || !actor.inquiry_form_fields) {
      return fieldId;
    }
    
    const field = actor.inquiry_form_fields.find(f => f.id === fieldId);
    return field ? field.label : fieldId;
  };

  const applyFilters = () => {
    let filtered = [...submissions];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(sub => {
        const searchLower = searchQuery.toLowerCase();
        // Search in form data
        const formDataMatch = Object.values(sub.form_data).some(value => 
          String(value).toLowerCase().includes(searchLower)
        );
        // Search in IP address
        const ipMatch = sub.ip_address && sub.ip_address.toLowerCase().includes(searchLower);
        return formDataMatch || ipMatch;
      });
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(sub => new Date(sub.submitted_at) >= fromDate);
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire day
      filtered = filtered.filter(sub => new Date(sub.submitted_at) <= toDate);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'submitted_at') {
        aVal = new Date(a.submitted_at);
        bVal = new Date(b.submitted_at);
      } else if (sortField === 'ip_address') {
        aVal = a.ip_address || '';
        bVal = b.ip_address || '';
      } else {
        aVal = a[sortField] || '';
        bVal = b[sortField] || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredSubmissions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;

    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(`${API}/admin/form-submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Submission deleted");
      fetchSubmissions();
      setSelectedIds(selectedIds.filter(id => id !== submissionId));
    } catch (error) {
      toast.error("Failed to delete submission");
      console.error(error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select submissions to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} submission(s)?`)) return;

    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        selectedIds.map(id => 
          axiosInstance.delete(`${API}/admin/form-submissions/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedIds.length} submission(s) deleted`);
      setSelectedIds([]);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete some submissions");
      console.error(error);
    }
  };

  const handleView = (submission) => {
    setSelectedSubmission(submission);
    setViewDialogOpen(true);
    
    // Mark submission as read when viewed
    if (!submission.is_read) {
      markAsRead(submission.id);
    }
  };

  const markAsRead = async (submissionId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.patch(`${API}/admin/form-submissions/${submissionId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setSubmissions(submissions.map(sub => 
        sub.id === submissionId ? { ...sub, is_read: true } : sub
      ));
    } catch (error) {
      console.error("Error marking submission as read:", error);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === paginatedSubmissions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedSubmissions.map(sub => sub.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const exportToCSV = (selectedOnly = false) => {
    const dataToExport = selectedOnly ? 
      filteredSubmissions.filter(sub => selectedIds.includes(sub.id)) : 
      filteredSubmissions;

    if (dataToExport.length === 0) {
      toast.error("No submissions to export");
      return;
    }

    // Get all unique field names
    const allFields = new Set();
    dataToExport.forEach(sub => {
      Object.keys(sub.form_data).forEach(key => allFields.add(key));
    });

    const headers = ['Submission Date', 'IP Address', ...Array.from(allFields)];
    const rows = dataToExport.map(sub => [
      new Date(sub.submitted_at).toLocaleString(),
      sub.ip_address || 'N/A',
      ...Array.from(allFields).map(field => {
        const value = sub.form_data[field];
        if (!value) return '';
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
      })
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-submissions-${actorId}-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  const exportToJSON = (selectedOnly = false) => {
    const dataToExport = selectedOnly ? 
      filteredSubmissions.filter(sub => selectedIds.includes(sub.id)) : 
      filteredSubmissions;

    if (dataToExport.length === 0) {
      toast.error("No submissions to export");
      return;
    }

    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-submissions-${actorId}-${Date.now()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Exported to JSON");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setSortField("submitted_at");
    setSortOrder("desc");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const generateEmbedCode = () => {
    if (!actor || !actor.inquiry_form_fields) {
      toast.error("No form configuration found");
      return;
    }

    const formFields = actor.inquiry_form_fields;
    
    // Generate the embed code
    const code = `<!-- AI Actor Inquiry Form Embed Code -->
<div id="ai-actor-form-${actorId}"></div>
<script>
(function() {
  const API_URL = '${BACKEND_URL}/api';
  const ACTOR_ID = '${actorId}';
  const CONTAINER_ID = 'ai-actor-form-${actorId}';
  
  const formFields = ${JSON.stringify(formFields, null, 2)};
  
  const styles = \`
    .ai-form-container { max-width: 600px; margin: 20px auto; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; font-family: system-ui, -apple-system, sans-serif; }
    .ai-form-title { font-size: 24px; font-weight: 600; margin-bottom: 20px; color: #1e293b; }
    .ai-form-group { margin-bottom: 16px; }
    .ai-form-label { display: block; margin-bottom: 6px; font-weight: 500; color: #334155; font-size: 14px; }
    .ai-form-required { color: #ef4444; margin-left: 4px; }
    .ai-form-help { font-size: 12px; color: #64748b; margin-top: 4px; }
    .ai-form-input, .ai-form-textarea, .ai-form-select { width: 100%; padding: 8px 12px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; transition: border-color 0.2s; }
    .ai-form-input:focus, .ai-form-textarea:focus, .ai-form-select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
    .ai-form-textarea { resize: vertical; min-height: 100px; }
    .ai-form-checkbox-group { display: flex; flex-direction: column; gap: 8px; }
    .ai-form-checkbox-item, .ai-form-radio-item { display: flex; align-items: center; gap: 8px; }
    .ai-form-checkbox, .ai-form-radio { width: 16px; height: 16px; cursor: pointer; }
    .ai-form-error { color: #ef4444; font-size: 12px; margin-top: 4px; }
    .ai-form-submit { background: #3b82f6; color: white; padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: background 0.2s; }
    .ai-form-submit:hover { background: #2563eb; }
    .ai-form-submit:disabled { background: #94a3b8; cursor: not-allowed; }
    .ai-form-success { background: #10b981; color: white; padding: 12px; border-radius: 6px; margin-top: 16px; }
  \`;
  
  function createForm() {
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;
    
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    
    let html = '<div class="ai-form-container"><h2 class="ai-form-title">Send Us an Inquiry</h2><form id="ai-inquiry-form">';
    
    formFields.forEach(field => {
      html += '<div class="ai-form-group">';
      html += \`<label class="ai-form-label">\${field.label}\`;
      if (field.required) html += '<span class="ai-form-required">*</span>';
      html += '</label>';
      
      if (field.type === 'textarea') {
        html += \`<textarea class="ai-form-textarea" name="\${field.id}" placeholder="\${field.placeholder || ''}" \${field.required ? 'required' : ''}></textarea>\`;
      } else if (field.type === 'select') {
        html += \`<select class="ai-form-select" name="\${field.id}" \${field.required ? 'required' : ''}>\`;
        html += '<option value="">Select an option</option>';
        (field.options || []).forEach(opt => {
          html += \`<option value="\${opt}">\${opt}</option>\`;
        });
        html += '</select>';
      } else if (field.type === 'checkbox') {
        if (field.options && field.options.length > 0) {
          html += '<div class="ai-form-checkbox-group">';
          field.options.forEach(opt => {
            html += \`<label class="ai-form-checkbox-item"><input type="checkbox" class="ai-form-checkbox" name="\${field.id}" value="\${opt}"> \${opt}</label>\`;
          });
          html += '</div>';
        } else {
          html += \`<label class="ai-form-checkbox-item"><input type="checkbox" class="ai-form-checkbox" name="\${field.id}" value="true"> Yes</label>\`;
        }
      } else if (field.type === 'radio') {
        html += '<div class="ai-form-checkbox-group">';
        (field.options || []).forEach(opt => {
          html += \`<label class="ai-form-radio-item"><input type="radio" class="ai-form-radio" name="\${field.id}" value="\${opt}" \${field.required ? 'required' : ''}> \${opt}</label>\`;
        });
        html += '</div>';
      } else {
        html += \`<input class="ai-form-input" type="\${field.type}" name="\${field.id}" placeholder="\${field.placeholder || ''}" \${field.required ? 'required' : ''}>\`;
      }
      
      if (field.helpText) {
        html += \`<div class="ai-form-help">\${field.helpText}</div>\`;
      }
      html += '</div>';
    });
    
    html += '<button type="submit" class="ai-form-submit">Submit</button></form></div>';
    container.innerHTML = html;
    
    document.getElementById('ai-inquiry-form').addEventListener('submit', handleSubmit);
  }
  
  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    
    const formData = new FormData(form);
    const data = {};
    
    for (let [key, value] of formData.entries()) {
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    }
    
    try {
      const response = await fetch(\`\${API_URL}/public/forms/\${ACTOR_ID}/submit\`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        form.innerHTML = '<div class="ai-form-success">Thank you! Your inquiry has been submitted successfully.</div>';
      } else {
        alert('Failed to submit. Please try again.');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
      }
    } catch (error) {
      alert('Failed to submit. Please try again.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit';
    }
  }
  
  createForm();
})();
</script>`;

    setEmbedCode(code);
    setEmbedDialogOpen(true);
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    toast.success("Embed code copied to clipboard!");
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading submissions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/ai/actors')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to AI Actors
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Form Submissions</h1>
            <p className="text-sm text-muted-foreground">
              {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''} 
              {filteredSubmissions.length !== submissions.length && ` (filtered from ${submissions.length})`}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={generateEmbedCode}
            variant="outline"
            size="sm"
            className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          >
            <Code className="w-4 h-4 mr-2" />
            Get Embed Code
          </Button>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="sm"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            onClick={() => exportToCSV(false)}
            variant="outline"
            size="sm"
            disabled={filteredSubmissions.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => exportToJSON(false)}
            variant="outline"
            size="sm"
            disabled={filteredSubmissions.length === 0}
          >
            <FileJson className="w-4 h-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Filters & Search</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                disabled={!searchQuery && !dateFrom && !dateTo}
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div>
              <Label>Search in submissions</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by any field or IP address..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>To Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Sort Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort By</Label>
                <Select value={sortField} onValueChange={setSortField}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submitted_at">Submission Date</SelectItem>
                    <SelectItem value="ip_address">IP Address</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">
                      <div className="flex items-center gap-2">
                        <SortDesc className="w-4 h-4" />
                        Descending (Newest First)
                      </div>
                    </SelectItem>
                    <SelectItem value="asc">
                      <div className="flex items-center gap-2">
                        <SortAsc className="w-4 h-4" />
                        Ascending (Oldest First)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.length} submission{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportToCSV(true)}
            >
              <Download className="w-4 h-4 mr-1" />
              Export Selected (CSV)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportToJSON(true)}
            >
              <FileJson className="w-4 h-4 mr-1" />
              Export Selected (JSON)
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkDelete}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Submissions Table */}
      {filteredSubmissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {submissions.length === 0 ? "No form submissions yet" : "No submissions match your filters"}
            </p>
            {submissions.length > 0 && filteredSubmissions.length === 0 && (
              <Button
                variant="link"
                onClick={clearFilters}
                className="mt-2"
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Submissions</span>
              <span className="text-sm font-normal text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">
                      <Checkbox
                        checked={selectedIds.length === paginatedSubmissions.length && paginatedSubmissions.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3 font-semibold text-sm">Date & Time</th>
                    <th className="text-left p-3 font-semibold text-sm">IP Address</th>
                    <th className="text-left p-3 font-semibold text-sm">Country</th>
                    <th className="text-right p-3 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubmissions.map((submission) => (
                    <tr key={submission.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.includes(submission.id)}
                          onCheckedChange={() => handleSelectOne(submission.id)}
                        />
                      </td>
                      <td className="p-3 text-sm">{formatDate(submission.submitted_at)}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {submission.ip_address || 'N/A'}
                      </td>
                      <td className="p-3 text-sm">
                        {ipCountries[submission.ip_address] || (
                          <span className="text-muted-foreground italic">Loading...</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(submission)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(submission.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={page === currentPage ? "" : "hidden sm:inline-flex"}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* View Submission Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Submission Details</DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              {/* Metadata */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Submitted At</p>
                  <p className="text-sm font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">IP Address</p>
                  <p className="text-sm font-medium">{selectedSubmission.ip_address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Country</p>
                  <p className="text-sm font-medium">
                    {ipCountries[selectedSubmission.ip_address] || 'Unknown'}
                  </p>
                </div>
              </div>

              {/* Form Data */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg border-b pb-2">Form Responses</h4>
                <div className="grid gap-4">
                  {(() => {
                    // Handle both nested and flat form_data structures
                    const formDataToDisplay = selectedSubmission.form_data?.form_data || selectedSubmission.form_data || {};
                    
                    return Object.entries(formDataToDisplay).map(([fieldId, value]) => {
                      // Skip if this is the nested form_data key itself
                      if (fieldId === 'form_data' && typeof value === 'object') {
                        return null;
                      }
                      
                      const label = getFieldLabel(fieldId);
                      return (
                        <div key={fieldId} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-base font-semibold text-slate-900">{label}</p>
                            {label !== fieldId && (
                              <span className="text-xs text-muted-foreground px-2 py-1 bg-white rounded border">
                                ID: {fieldId}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 p-3 bg-white rounded border border-slate-200">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                              {typeof value === 'object' ? (
                                <code className="text-xs">{JSON.stringify(value, null, 2)}</code>
                              ) : (
                                String(value)
                              )}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {(() => {
                    const formDataToDisplay = selectedSubmission.form_data?.form_data || selectedSubmission.form_data || {};
                    return Object.keys(formDataToDisplay).filter(k => k !== 'form_data').length === 0 && (
                      <p className="text-sm text-muted-foreground italic text-center py-8">
                        No form data submitted
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* User Agent */}
              {selectedSubmission.user_agent && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground">User Agent</p>
                  <p className="text-xs text-slate-600 mt-1">{selectedSubmission.user_agent}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Embed Form Code</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Copy and paste this code into your website's HTML to embed the inquiry form. 
                The form will automatically submit to your AI Actor and send email notifications.
              </p>
              
              <div className="relative">
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-xs">
                  <code>{embedCode}</code>
                </pre>
                <Button
                  onClick={copyEmbedCode}
                  className="absolute top-2 right-2"
                  size="sm"
                >
                  <Code className="w-4 h-4 mr-2" />
                  Copy Code
                </Button>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">How to use:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Copy the embed code above</li>
                <li>Paste it into your website's HTML where you want the form to appear</li>
                <li>The form will render automatically with all configured fields</li>
                <li>Form submissions will be sent to this dashboard and email notifications will be triggered</li>
              </ol>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-semibold text-amber-900 mb-2">⚠️ Important Notes:</h4>
              <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                <li>Make sure your website allows external scripts to run</li>
                <li>The form styling is self-contained and won't conflict with your site's CSS</li>
                <li>Form submissions are tracked with IP address and timestamp</li>
                <li>You can customize the form fields in the AI Actor's Advanced settings</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
